import ArticleLayout, { P, H2, UL, LI, Strong, Note } from '../../components/ArticleLayout.jsx';

export default function WhyTheScaleLies({ related }) {
  return (
    <ArticleLayout
      title="Why the scale isn't your progress"
      summary="Your body weight can swing 4–6 lbs in a single day for reasons that have nothing to do with fat. Here's what's actually moving the number, why daily weigh-ins drive people crazy, and how to read the scale so it tells you the truth."
      readingMin={6}
      date="2026-05-22"
      related={related}
    >
      <P>
        You weigh in Monday morning at 178. Tuesday morning, you're 181. You haven't eaten anything wildly different and you trained hard yesterday. Did you gain 3 lbs of fat? Of course not — 3 lbs of fat requires a 10,500 calorie surplus, and you didn't eat 10,500 extra calories in a day. So what moved?
      </P>

      <H2>What the scale actually measures</H2>
      <P>
        The scale weighs everything: bones, organs, muscle, fat, the water in your blood, the food in your stomach, the glycogen in your muscles, and whatever you haven't passed yet. Of these, only one (fat) changes slowly. The rest can swing several pounds in a day.
      </P>
      <Note>
        Body fat changes at a maximum rate of about 0.5 lbs per day in either direction, and only under aggressive conditions. Anything faster than that on the scale is water, glycogen, or gut contents — not fat.
      </Note>

      <H2>The five things that move your scale weight day to day</H2>
      <UL>
        <LI><Strong>Glycogen + water (1–3 lbs).</Strong> Every gram of stored carbohydrate (glycogen) in your muscles pulls 3 grams of water with it. A high-carb day adds 2–3 lbs of weight that isn't fat; a low-carb day or a hard cardio session drops it.</LI>
        <LI><Strong>Sodium (1–4 lbs).</Strong> Salt makes you retain water — anywhere from a few hours to a few days. A salty dinner shows up the next morning as 1–3 extra lbs that disappear by Wednesday.</LI>
        <LI><Strong>Hormonal water retention (2–6 lbs).</Strong> Most pronounced in women during the week before menstruation, but everyone has hormonal swings. Cortisol from stress (or from a hard training week) causes water retention too.</LI>
        <LI><Strong>Training-induced inflammation (1–3 lbs).</Strong> Hard leg day causes microscopic muscle damage. Your body pumps water into those muscles for repair. The scale goes up for 24–72 hours, but you haven't gained anything — the muscle isn't bigger, it's just temporarily inflamed.</LI>
        <LI><Strong>Gut contents (1–4 lbs).</Strong> Whatever's in your digestive tract weighs something. A high-fiber day, missed bathroom trip, or large meal the night before all show up on the morning scale.</LI>
      </UL>
      <P>
        Add all of these up and a single day can swing ±4 lbs from your actual body composition. Comparing Monday's weight to Tuesday's tells you almost nothing about fat loss or muscle gain.
      </P>

      <H2>The fix: weekly averages</H2>
      <P>
        Daily fluctuations cancel out across a week. Weigh in 3–4 mornings a week (same time, after using the bathroom, before eating or drinking) and average them. Compare this week's average to last week's average — not yesterday's number to today's.
      </P>
      <Note>
        A 0.5 lb drop in your weekly average is a meaningful trend. A 2 lb drop in your daily number is meaningless noise. TrainForge plots the trend line for you on the Progress page so the daily noise gets filtered out automatically.
      </Note>

      <H2>Specific patterns to expect</H2>
      <UL>
        <LI><Strong>Starting a cut:</Strong> Big drop in week 1 (3–5 lbs), mostly glycogen + water. Then 1–1.5 lbs/week of actual fat loss after that.</LI>
        <LI><Strong>Starting a bulk:</Strong> Big jump in week 1 (2–4 lbs), mostly glycogen + water. Then 0.5 lb/week of actual lean gain plus some fat.</LI>
        <LI><Strong>After a heavy training day:</Strong> Scale up 1–3 lbs the next morning from muscle inflammation. Comes back down in 1–3 days.</LI>
        <LI><Strong>After a high-carb refeed or cheat meal:</Strong> Scale up 2–5 lbs for 24–48 hours from glycogen + sodium + gut contents. Not fat gain unless the meal was thousands of calories above maintenance.</LI>
        <LI><Strong>Travel or stressful week:</Strong> 2–4 lbs of water retention from cortisol + sleep disruption + different food. Resolves within a week of returning to normal routine.</LI>
      </UL>

      <H2>When the scale is the right tool</H2>
      <P>
        For pure fat loss goals where you want to lose 1+ lbs/week, the scale (averaged weekly) is the easiest and cheapest progress tool you have. It's a perfectly fine measurement if you read it correctly.
      </P>

      <H2>When the scale is the wrong tool</H2>
      <UL>
        <LI><Strong>Recomp goals:</Strong> The whole point is that scale weight stays flat while body composition changes. Use photos and tape measurements instead.</LI>
        <LI><Strong>Slow lean gain:</Strong> 0.5 lb/week of muscle gain is invisible under normal weight fluctuation. Strength PRs are a better indicator.</LI>
        <LI><Strong>Maintenance phases:</Strong> Your weight should bounce in a 2-3 lb range around your target. Worrying about individual readings inside that range will drive you insane.</LI>
      </UL>

      <H2>The bottom line</H2>
      <P>
        Daily scale readings are noise. Weekly averages are signal. If you've never seen the difference, try this: weigh yourself every morning for two weeks, plot the numbers, then draw a line through the middle. The line tells you what your body is actually doing. The dots tell you what you ate yesterday.
      </P>
    </ArticleLayout>
  );
}
